/**
 * Platform Mapping Utilities
 * Maps game engine/code language settings to export template platforms
 */

import { Platform } from '@/types/exportTemplates';
import { Game } from '@/lib/stores/storeTypes';

/**
 * Map game engine and code language to export template platforms
 */
export function getAvailablePlatformsForGame(game: Game | null): Platform[] {
  if (!game) {
    return ['roblox', 'cpp', 'unity', 'godot', 'json', 'custom'];
  }

  const engine = game.settings?.engine?.toLowerCase();
  const codeLanguage = game.settings?.codeLanguage?.toLowerCase();
  
  const platforms = new Set<Platform>();

  // Map based on engine
  switch (engine) {
    case 'roblox':
      platforms.add('roblox');
      break;
    
    case 'unity':
      platforms.add('unity');
      // Unity can use C# or C++
      if (codeLanguage === 'cpp') {
        platforms.add('cpp');
      }
      break;
    
    case 'unreal':
    case 'cryengine':
      platforms.add('cpp');
      break;
    
    case 'godot':
      platforms.add('godot');
      // Godot can use GDScript or C++
      if (codeLanguage === 'cpp') {
        platforms.add('cpp');
      }
      break;
    
    default:
      // No specific engine set
      break;
  }

  // Map based on code language (if not already added by engine)
  switch (codeLanguage) {
    case 'lua':
      platforms.add('roblox');
      break;
    
    case 'cpp':
      platforms.add('cpp');
      break;
    
    case 'csharp':
      platforms.add('unity');
      break;
    
    case 'gdscript':
      platforms.add('godot');
      break;
    
    default:
      break;
  }

  // If no specific platform detected, allow all
  if (platforms.size === 0) {
    return ['roblox', 'cpp', 'unity', 'godot', 'json', 'custom'];
  }

  // Always allow json and custom
  platforms.add('json');
  platforms.add('custom');

  return Array.from(platforms);
}

/**
 * Check if a platform is available for a game
 */
export function isPlatformAvailableForGame(platform: Platform, game: Game | null): boolean {
  const availablePlatforms = getAvailablePlatformsForGame(game);
  return availablePlatforms.includes(platform);
}

/**
 * Map view display modes to platforms for blueprint/glossary code preview
 */
export type ViewDisplayMode = 'structured' | 'unity' | 'godot' | 'cpp' | 'typescript' | 'roblox' | 'json';

export function getAvailableViewModesForGame(game: Game | null): ViewDisplayMode[] {
  // Always allow structured and json
  const modes: ViewDisplayMode[] = ['structured', 'json'];
  
  const platforms = getAvailablePlatformsForGame(game);
  
  // Map platforms to view modes
  if (platforms.includes('unity')) {
    modes.push('unity');
  }
  
  if (platforms.includes('godot')) {
    modes.push('godot');
  }
  
  if (platforms.includes('cpp')) {
    modes.push('cpp');
  }
  
  if (platforms.includes('roblox')) {
    modes.push('roblox');
  }
  
  // TypeScript is generally available for web-based engines
  if (game?.settings?.engine) {
    const engine = game.settings.engine.toLowerCase();
    if (engine === 'web-gl' || engine === 'custom' || !engine) {
      modes.push('typescript');
    }
  } else {
    modes.push('typescript'); // Default to showing if no engine set
  }
  
  return modes;
}

/**
 * Get the default platform for a game (used when no active template is set)
 */
export function getDefaultPlatformForGame(game: Game | null): Platform {
  const availablePlatforms = getAvailablePlatformsForGame(game);
  
  // Prefer the first available platform
  if (availablePlatforms.length > 0 && availablePlatforms[0] !== 'custom') {
    return availablePlatforms[0];
  }
  
  // Fallback to roblox
  return 'roblox';
}

